/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";
import Text from "../ui/Text";
import Flex from "../ui/Flex";
import Stack from "../ui/Stack";
import Icon from "../ui/Icon";
import Button from "../ui/Button";
import TextCircleDivider from "../ui/TextCircleDivider";
import InvoiceStatusBadge from "../InvoiceStatusBadge";
import FormatPrice from "../formatters/FormatPrice";
import FormatDateTime from "../formatters/FormatDateTime";
import { getInvoiceRoute } from "../../utils/helpers/invoice";
import { useClickableA11y } from "../../hooks/a11y/useClickableA11y";
import { useTranslate } from "../../hooks/useTranslate";

const StyledClientInvoiceItem = styled(Flex)`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  background: rgba(${({ theme }) => theme.colors.accent.rgb}, 0.05);
  transition: background-color 0.2s ease;

  ${({ $clickable, theme }) =>
    $clickable &&
    css`
      cursor: pointer;

      @media (hover: hover) {
        &:hover {
          background: rgba(${theme.colors.accent.rgb}, 0.1);
        }
      }
    `}

  ${({ theme }) => theme.containerMedia.sm} {
    gap: 0.5rem;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const StyledClientInvoiceData = styled(Flex)`
  ${({ theme }) => theme.containerMedia.sm} {
    width: 100%;
  }
`;

const StyledClientInvoicePrint = styled(Button)`
  ${({ theme }) => theme.containerMedia.sm} {
    margin-left: auto;
  }
`;

const ClientInvoiceItem = ({ invoice }) => {
  const navigate = useNavigate();
  const { _t } = useTranslate();
  const route = getInvoiceRoute(invoice);
  const handleClick = route ? () => navigate(route) : undefined;
  const a11yProps = useClickableA11y({ onClick: handleClick });

  return (
    <StyledClientInvoiceItem $align="center" $justify="space-between" $gap="1rem" onClick={handleClick} $clickable={!!route} {...a11yProps}>
      <Stack $gap="0.25rem">
        <Text color="dark" weight="bold">
          {invoice.number}
        </Text>
        <Flex $align="center" $gap="0.25rem">
          <Text size="small">
            <FormatDateTime value={invoice.datetime} format={FormatDateTime.FORMAT.DATE} />
          </Text>
          <TextCircleDivider size={2} color="textSecondary" />
          <Text size="small">{invoice.payment_processor}</Text>
        </Flex>
      </Stack>

      <StyledClientInvoiceData $align="center" $gap="1rem">
        <InvoiceStatusBadge status={invoice.status} payment_received={invoice.payment_received} />
        <Text color="dark" weight="bold">
          <FormatPrice value={invoice.amount} currency={invoice.currency} />
        </Text>
        {invoice.print_link && (
          <StyledClientInvoicePrint
            variant="outline"
            size="icon"
            icon={<Icon.Download />}
            href={invoice.print_link}
            target="_blank"
            aria-label={_t("print")}
            onClick={(event) => event.stopPropagation()}
          />
        )}
      </StyledClientInvoiceData>
    </StyledClientInvoiceItem>
  );
};

export default ClientInvoiceItem;
